import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchForm, FormResponse } from '../../shared/api/types';
import { Alert, Button, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { APPROVAL_BADGE, APPROVAL_STATUS } from '../approvals/labels';
import { formatAnswer, responsesCsv } from './labels';

/** «Por aprobar» se decide ahí mismo en la tabla (#71); «No aprobar» pide el motivo. */
function ApprovalCell({ churchId, response: r, onChange }: { churchId: string; response: FormResponse; onChange: () => void }) {
  const me = useMe();
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const a = r.approval;
  if (!a) return null;
  const badge = <span className={APPROVAL_BADGE[a.status]}>{APPROVAL_STATUS[a.status]}</span>;
  if (a.status !== 'pending' || !me.can('approvals.decide') || r.person.personId === me.personId) return badge;

  async function decide(path: 'approve' | 'reject', note: string | null) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/approvals/churches/${churchId}/requests/${a!.requestId}/${path}`, { note });
      onChange();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  function reject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const note = String(new FormData(event.currentTarget).get('note') ?? '').trim();
    if (!note) return setError('Escribe el motivo.');
    void decide('reject', note);
  }

  return (
    <div className="approval-cell">
      {badge}
      {rejecting ? (
        <form className="form" onSubmit={reject} noValidate>
          <label className="visually-hidden" htmlFor={`motivo-${r.id}`}>
            Motivo para no aprobar a {r.person.name}
          </label>
          <input id={`motivo-${r.id}`} name="note" maxLength={1000} placeholder="Motivo (lo verá quien respondió)" autoFocus />
          <div className="actions">
            <Button type="submit" variant="secondary" busy={busy}>
              No aprobar
            </Button>
            <Button type="button" variant="ghost" onClick={() => setRejecting(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <div className="actions">
          <Button busy={busy} onClick={() => void decide('approve', null)} aria-label={`Aprobar a ${r.person.name}`}>
            Aprobar
          </Button>
          <Button variant="secondary" onClick={() => setRejecting(true)} aria-label={`No aprobar a ${r.person.name}`}>
            No aprobar
          </Button>
        </div>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

/** Respuestas de todos (datos personales: el acceso queda auditado). */
export function FormResponsesPage() {
  const { formId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) =>
      churchId
        ? api.get<{ form: ChurchForm; responses: FormResponse[] }>(`/forms/churches/${churchId}/forms/${formId}/responses`, signal)
        : Promise.reject(new Error('Sin contexto')),
    [churchId, formId],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  function download(form: ChurchForm, responses: FormResponse[]) {
    const url = URL.createObjectURL(new Blob([responsesCsv(form, responses)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.title.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page">
      <p className="small">
        <Link to="/app/formularios">← Formularios</Link>
      </p>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        <>
          <header className="page__header">
            <h1>{data.data.form.title}</h1>
            <p className="muted">{data.data.responses.length === 1 ? '1 respuesta' : `${data.data.responses.length} respuestas`}</p>
          </header>
          <Alert tone="info">Contiene datos personales. Tu acceso queda registrado; compártelos solo con quien los necesite.</Alert>
          {data.data.responses.length === 0 ? (
            <EmptyState title="Aún no hay respuestas." />
          ) : (
            <>
              <div className="actions">
                <Button variant="secondary" onClick={() => download(data.data.form, data.data.responses)}>
                  Descargar para Excel
                </Button>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Persona</th>
                      <th scope="col">Fecha</th>
                      {data.data.form.requiresApproval ? <th scope="col">Aprobación</th> : null}
                      {data.data.form.fields.map((f) => (
                        <th key={f.key} scope="col">
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.responses.map((r) => (
                      <tr key={r.id}>
                        <td>{r.person.name}</td>
                        <td>{new Date(r.submittedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })}</td>
                        {data.data.form.requiresApproval ? (
                          <td>
                            <ApprovalCell churchId={churchId} response={r} onChange={data.reload} />
                          </td>
                        ) : null}
                        {data.data.form.fields.map((f) => (
                          <td key={f.key}>{formatAnswer(f, r.answers[f.key])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
